import * as React from "react";

import {
  type ActivityPeriod,
  type FilledActivityBucket,
  type InvocationGroupBy,
  InvocationStatus,
  type TimeWindow,
  fillActivityBuckets,
} from "@/shared/domain/actions";
import {
  useInvocationActivity,
  useInvocationBreakdown,
  useInvocationQueueStats,
} from "@/shared/domain/actions/hooks";
import type { Condition } from "@/types";

/** Holding a compute slot: the same statuses `runningCount` adds up, asked of the breakdown API. */
const SLOT_HOLDING = [
  InvocationStatus.Scheduled,
  InvocationStatus.Downloading,
  InvocationStatus.Processing,
  InvocationStatus.Uploading,
];

const TERMINAL = [
  InvocationStatus.Completed,
  InvocationStatus.Failed,
  InvocationStatus.Deadly,
  InvocationStatus.Cancelled,
];

/**
 * What the four cards show, and what the collapsed heading condenses into one line.
 *
 * Only meaningful once every query has landed: read it behind the `isPending` and `isError` this
 * hook returns, which is what tells a null `concurrencyLimit` (the org has no cap) apart from a
 * limit nothing has fetched yet.
 */
export interface PanelFigures {
  concurrencyLimit: number | null;
  /** Everything running, not only what fits in the running breakdown's groups. */
  runningNow: number;
  /** Everything waiting, not only what fits in the waiting breakdown's groups. */
  waitingNow: number;
  finished: number;
  failed: number;
  credits: number;
}

export interface ActivityPanelDataOptions {
  window: TimeWindow;
  /** Omit for the finest bucket the window allows. */
  period: ActivityPeriod | undefined;
  condition: Condition | undefined;
  /** Dimensions the waiting breakdown is grouped by. */
  groupBy: InvocationGroupBy[];
  refetchInterval: number | false;
}

/**
 * Everything the panel draws, from four queries that refresh together: the window's buckets, the
 * headline figures, and what is waiting right now. "Now" comes from live counts rather than from
 * the window, so the running and waiting figures stay true when the reader is looking at the past.
 *
 * The four are reported as one state. A panel drawn from three of them looks exactly like a panel
 * drawn from four, so a reader would have no way to tell a figure that is missing from one that is
 * genuinely zero.
 */
export function useActivityPanelData({
  window: timeWindow,
  period,
  condition,
  groupBy,
  refetchInterval,
}: ActivityPanelDataOptions) {
  const activity = useInvocationActivity({
    window: timeWindow,
    period,
    condition,
    refetchInterval,
  });
  const queueStats = useInvocationQueueStats({ refetchInterval });
  const waiting = useInvocationBreakdown({
    statuses: [InvocationStatus.Queued],
    groupBy,
    condition,
    limit: 10,
    refetchInterval,
  });
  const running = useInvocationBreakdown({
    statuses: SLOT_HOLDING,
    groupBy: ["action"],
    condition,
    limit: 100,
    refetchInterval,
  });

  const buckets = React.useMemo(
    () => (activity.data ? fillActivityBuckets(activity.data) : []),
    [activity.data],
  );
  const figures: PanelFigures = {
    concurrencyLimit: queueStats.data?.concurrency_limit ?? null,
    runningNow: running.data?.total_count ?? 0,
    waitingNow: waiting.data?.total_count ?? 0,
    finished: sumEntered(buckets, TERMINAL),
    failed: sumEntered(buckets, [
      InvocationStatus.Failed,
      InvocationStatus.Deadly,
    ]),
    credits:
      buckets.reduce(
        (total, bucket) => total + bucket.computeCreditMillionths,
        0,
      ) / 1_000_000,
  };

  const isPending =
    activity.isPending ||
    queueStats.isPending ||
    waiting.isPending ||
    running.isPending;

  return {
    activity,
    queueStats: queueStats.data,
    waitingGroups: waiting.data?.groups ?? [],
    buckets,
    figures,
    isPending,
    /**
     * Whether what is returned answers an earlier request: the window or condition changed and
     * the new fetch is still running, so the old data stands in. A refresh of the same request
     * never sets this. The queue stats take no window or condition, so they cannot go stale
     * this way.
     */
    isPlaceholder:
      activity.isPlaceholderData ||
      waiting.isPlaceholderData ||
      running.isPlaceholderData,
    isError:
      activity.isError ||
      queueStats.isError ||
      waiting.isError ||
      running.isError ||
      (!isPending && activity.data === undefined),
  };
}

function sumEntered(
  buckets: FilledActivityBucket[],
  statuses: InvocationStatus[],
): number {
  return buckets.reduce(
    (total, bucket) =>
      total +
      statuses.reduce(
        (inner, status) =>
          inner + (bucket.enteredStatus[InvocationStatus[status]] ?? 0),
        0,
      ),
    0,
  );
}
